import { useCallback, useEffect, useRef } from "react";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import { DocumentsTaskAskButton } from "../../DocumentsTaskAskButton";

interface Props {
    selectedCount: number;
    selectedDocumentCount: number;
    canRemoveFromTask: boolean;
    canMarkStatementUnavailable: boolean;
    canAskAbout?: boolean;
    isRemoving: boolean;
    onClearSelection: () => void;
    onRemoveFromTask: () => void;
    onMarkStatementUnavailable: () => void;
    onAskAbout?: () => void;
}

export function DocumentRetrievalBulkActions({
    selectedCount,
    selectedDocumentCount,
    canRemoveFromTask,
    canMarkStatementUnavailable,
    canAskAbout,
    isRemoving,
    onClearSelection,
    onRemoveFromTask,
    onMarkStatementUnavailable,
    onAskAbout,
}: Readonly<Props>) {
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const { open, registerOpenOverride, unregisterOpenOverride } =
        useCommands();

    const hasBulkActions =
        canRemoveFromTask || canMarkStatementUnavailable || canAskAbout;

    const handleOpenBulkActions = useCallback(() => {
        if (!hasBulkActions) {
            return;
        }
        open(CommandsDirectory.DOCUMENT_RETRIEVAL_BULK, {
            selectedDocumentRetrievalCount: selectedCount,
            onDocumentRetrievalBulkRemoveFromTask: canRemoveFromTask
                ? () => {
                      if (refuseIfViewOnly() || isRemoving) {
                          return;
                      }
                      onRemoveFromTask();
                  }
                : undefined,
            onDocumentRetrievalBulkStatementUnavailable:
                canMarkStatementUnavailable
                    ? () => {
                          if (refuseIfViewOnly()) {
                              return;
                          }
                          onMarkStatementUnavailable();
                      }
                    : undefined,
            onDocumentRetrievalBulkAskAbout:
                canAskAbout && onAskAbout ? onAskAbout : undefined,
        });
    }, [
        canAskAbout,
        canMarkStatementUnavailable,
        canRemoveFromTask,
        hasBulkActions,
        isRemoving,
        onAskAbout,
        onMarkStatementUnavailable,
        onRemoveFromTask,
        open,
        refuseIfViewOnly,
        selectedCount,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (!hasBulkActions || isViewOnly) {
            unregisterOpenOverride();
            return;
        }
        registerOpenOverride(() => handleOpenBulkActionsRef.current());
        return () => unregisterOpenOverride();
    }, [
        hasBulkActions,
        isViewOnly,
        registerOpenOverride,
        unregisterOpenOverride,
    ]);

    return (
        <>
            <HStack gap={8}>
                <Tooltip content={`Deselect ${selectedCount}`}>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={onClearSelection}
                    >
                        {selectedCount} selected
                    </Button>
                </Tooltip>
            </HStack>
            {hasBulkActions && (
                <HStack gap={8}>
                    {canAskAbout && onAskAbout && (
                        <DocumentsTaskAskButton
                            selectedCount={selectedDocumentCount}
                            onAskAbout={onAskAbout}
                        />
                    )}
                    <Tooltip content="Open bulk actions">
                        <Button
                            variant="primary"
                            size="md"
                            disabled={isViewOnly}
                            onClick={handleOpenBulkActions}
                            rightIcon={
                                <span data-slot-box="">
                                    <Shortcut keys="mod+k" />
                                </span>
                            }
                        >
                            Actions
                        </Button>
                    </Tooltip>
                </HStack>
            )}
        </>
    );
}
